// Simple client-side validator to check forward and reverse mappings

export function validateMappingsForEntity({
  entityType,
  rulesForType,
  populatedSections,
  relationships,
  entities,
  editingEntity,
}) {
  const report = {
    entityType,
    totalRules: (rulesForType || []).length,
    missingSections: [],
    missingIncomingForDbRels: [],
    missingOutgoingForDbRels: [],
  };

  const sectionKeys = new Set(Object.keys(populatedSections || {}));

  // 1) For each transformed rule for this entity type, ensure a section exists
  (rulesForType || []).forEach((rule) => {
    // Known explicit mapping keys live in rule.key for outgoing, and `${rule.rule_key}_reverse` for incoming at source
    // Here we only have the transformed item, so derive a likely key presence by title + relatedType
    // We can at least verify a section exists for this rule's related type by scanning populatedSections metadata
    const hasSection = Array.from(sectionKeys).some((k) => {
      const s = populatedSections[k];
      if (!s) return false;
      // Match by related type and direction
      const typeMatch =
        (s.relatedType || "").toLowerCase() ===
        (rule.relatedEntityType || "").toLowerCase();
      const dirMatch = !!(rule.isIncoming
        ? k.includes("_reverse") || s.direction === "incoming"
        : s.direction !== "incoming");
      return typeMatch && dirMatch;
    });
    if (!hasSection) {
      report.missingSections.push({
        key: rule.key,
        direction: rule.isIncoming ? "incoming" : "outgoing",
        relatedType: rule.relatedEntityType,
        label: rule.visibleLabel,
      });
    }
  });

  // 2) For each DB relationship involving the current entity, ensure there is a section that can show it
  const entityId = editingEntity?.id;
  const typeMap = (t) => (t || "").toLowerCase();

  (relationships || []).forEach((rel) => {
    if (!entityId) return;
    if (rel.source_entity_id !== entityId && rel.target_entity_id !== entityId)
      return;

    const isIncoming = rel.target_entity_id === entityId;
    // Resolve related entity type
    const otherId = isIncoming ? rel.source_entity_id : rel.target_entity_id;
    const other = (entities || []).find((e) => e.id === otherId);
    const otherType = typeMap(other?.entity_type || other?.type);

    const match = Array.from(sectionKeys).some((k) => {
      const s = populatedSections[k];
      if (!s) return false;
      // Relate flexible type matching: allow minor naming variations
      const rt = typeMap(s.relatedType);
      const typeMatches =
        !!rt && (otherType.includes(rt) || rt.includes(otherType));
      const dirMatches = isIncoming
        ? k.includes("_reverse") || s.direction === "incoming"
        : s.direction !== "incoming";
      return typeMatches && dirMatches;
    });

    if (!match) {
      const bucket = isIncoming
        ? report.missingIncomingForDbRels
        : report.missingOutgoingForDbRels;
      bucket.push({
        rel_id: rel.id,
        type: rel.type,
        other_entity_type: otherType,
        direction: isIncoming ? "incoming" : "outgoing",
      });
    }
  });

  return report;
}
